import { useCallback } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { requireGlobalCapability } from '@/lib/route-guards';
import {
  parseAdminConfigSearch,
  serializeAdminConfigSearch,
  updateAdminConfigSearch,
} from '@/screens/admin-config-navigation';
import type { AdminConfigSearch } from '@/screens/admin-config-navigation';
import { AdminConfigView } from '@/screens/admin-config';

export const Route = createFileRoute('/admin/config')({
  beforeLoad: requireGlobalCapability('config_manage', 'full'),
  validateSearch: parseAdminConfigSearch,
  // Route-data exemption: realtime. The config document polls; blocking
  // first paint on a read that will arrive again in two seconds trades a fast paint for
  // nothing.
  component: AdminConfigPage,
});

function AdminConfigPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  // Stable on purpose: the view's validation-navigation effect depends on this
  // callback, and a fresh arrow per render would re-run that effect every render.
  const onSearchChange = useCallback((
    patch: Partial<AdminConfigSearch>,
    options?: { replace?: boolean },
  ) => {
    void navigate({
      // Write the complete validated state on every navigation. Section
      // and tab changes push by default so browser history restores the
      // exact editor location; corrective moves (validation failures) replace.
      search: (current) => serializeAdminConfigSearch(
        updateAdminConfigSearch(current, patch),
      ),
      replace: options?.replace,
    });
  }, [navigate]);

  return <AdminConfigView search={search} onSearchChange={onSearchChange} />;
}
